import { ApiError } from "../api/client.js";
import type { AccountRef, AccountView, AmountDerivation, BundleSummary, BundleView, RefusedStep, SignedStep } from "../api/types.js";
import { amountOf, whatOf } from "../app/edit-model.js";
import { differenceMoney, formatMoney, fromMinor, lastDigits, sumMoney, toMinor } from "../lib/format.js";

/**
 * DCTA-170 — the card's edits, made by the bank: the one seam between the app and the amend route (backend B46, not
 * built yet; its contract is docs/design/v3/03-edit-in-place.md §0.1). Wiring the real route is this file's flag and
 * nothing else: the controller (`app/editor.ts`) only ever sees an `AmendResult`.
 *
 * Each edit goes to the bank on its own; the bank applies it to the stored plan (never to model output), runs every rule
 * again, and answers a NEW bundle — a new id, challenge, hash and expiry — or refuses it all (the card on the screen
 * stays signable). The app never builds a figure: what it shows after an edit is the bank's new bundle, read back.
 *
 * Two sources, one contract: `routeAmend` (the bank's route, B46 — DCTA-171, the default) and `fakeAmend` (an in-process
 * stand-in for the bank: the design review with `?amend=fake` on this machine, and the unit tests). A card the stand-in
 * made exists only in this tab — the gateway does not know it, so it cannot be signed: the stand-in is never the default.
 */

export type AmendEdit =
  /**
   * `dependants: "keep"` (an ask to B46, from §2.4's "Keep the order at S$500.00" on a change): a step that is a share of this
   * one keeps its figure as the customer's own instead of following. Absent: it follows, as §0.1 says.
   */
  | { readonly op: "set_amount"; readonly step_id: string; readonly amount: { readonly value: string; readonly currency: "SGD" }; readonly dependants?: "keep" }
  | { readonly op: "remove"; readonly step_id: string; readonly dependants: "remove" | "keep" }
  | { readonly op: "set_source"; readonly step_id: string; readonly account_id: string }
  | { readonly op: "restore"; readonly step_id: string; readonly from_bundle_id: string };

/** One thing the bank changed (§0.1 `changes[]`): its own words for the step (`what`), and the figures it moved. */
export interface AmendChange {
  readonly step_id: string;
  readonly change: "amount" | "removed" | "kept" | "restored" | "source" | (string & {});
  readonly what: string;
  readonly before?: string;
  readonly after?: string;
  readonly because?: string;
}

export type AmendResult =
  | { readonly ok: true; readonly bundle: BundleView; readonly amendedFrom: string; readonly changes: readonly AmendChange[] }
  /**
   * 422 AMEND_REFUSED: a rule refused an edited step; nothing was built and the card on the screen stays signable — unless
   * the bank stopped cards for a Yes it carries (H7: `SCAM_CHECK_PAUSED`; `cancelled` names them, the card itself among them).
   */
  | { readonly ok: false; readonly why: "refused"; readonly refused: readonly RefusedStep[]; readonly cancelled?: readonly string[] }
  /** 422 AMEND_WOULD_EMPTY (H5): the edit would leave nothing on the card — that is cancelling it (22w, the cancel route). */
  | { readonly ok: false; readonly why: "would-empty" }
  /** 409 AMEND_STALE: the card changed under the edit (another tab); `latest` is the card read again, when it reads. */
  | { readonly ok: false; readonly why: "stale"; readonly latest?: BundleView }
  /** 409 BUNDLE_NOT_AWAITING: signed, cancelled, expired or superseded — the card's real end is drawn. */
  | { readonly ok: false; readonly why: "not-awaiting" }
  /** The network, 429, a 5xx, or AMEND_INVALID (a client bug): row 22x's line, the card as it was. */
  | { readonly ok: false; readonly why: "unreachable" };

export type PreviewResult =
  /** `wouldEmpty` (H5): the edit would leave nothing on the card — ask 22w, never the dependants sheet. */
  | { readonly ok: true; readonly changes: readonly AmendChange[]; readonly words: string; readonly wouldEmpty?: true }
  | Exclude<AmendResult, { readonly ok: true }>;

export interface AmendSource {
  /** false: the stand-in — its cards exist only in this tab, so the app fetches no signing options for them. */
  readonly signable?: boolean;
  /**
   * An edit of the card on the screen (`current`), applied by the bank. One per call from the app, except "Keep it as it
   * was" (§0.2: every removal restored and every figure set back, in one apply). Never rejects.
   */
  apply(current: BundleView, edits: AmendEdit | readonly AmendEdit[]): Promise<AmendResult>;
  /** What the edit would change and the bank's sentence for the dependants sheet (22q); stores nothing. Never rejects. */
  preview(current: BundleView, edit: AmendEdit): Promise<PreviewResult>;
}

/** This machine: the dev server, the e2e gateway, a designer's local staging — never a public host. */
const LOCAL = /^(localhost|127\.0\.0\.1|\[::1\]|[a-z0-9-]+\.localhost|[a-z0-9.-]+\.test)$/i;

/**
 * The bank's route by default (B46 shipped, DCTA-171) · `?amend=off`: Edit goes to the conversation, as before B46 ·
 * `?amend=fake`: the stand-in, which draws the edited card itself — the app's figures, not the bank's — so it answers
 * only on this machine (`host`); a link carrying it to the public demo gets the bank's route.
 */
export function amendFlag(search: string, host: string): "fake" | "route" | "off" {
  const v = new URLSearchParams(search).get("amend");
  if (v === "off") return "off";
  if (v === "fake" && LOCAL.test(host)) return "fake";
  return "route";
}

// ---------------------------------------------------------------- the bank's route (B46)

/** The two reads the route client needs: `POST /v1/bundles/{id}/amend` and `GET /v1/bundles/{id}` (api/client.ts). */
export interface AmendApi {
  amend(bundleId: string, body: { readonly base_sha256: string; readonly mode: "apply" | "preview"; readonly edits: readonly AmendEdit[] }): Promise<Record<string, unknown>>;
  bundle(bundleId: string): Promise<BundleView>;
}

function failure(error: unknown): Exclude<AmendResult, { readonly ok: true }> {
  if (error instanceof ApiError) {
    if (error.reasonCode === "AMEND_REFUSED") {
      const refused = Array.isArray(error.body["refused_steps"]) ? (error.body["refused_steps"] as RefusedStep[]) : [];
      const ids = error.body["cancelled_bundle_ids"];
      const cancelled = Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string") : [];
      return cancelled.length === 0 ? { ok: false, why: "refused", refused } : { ok: false, why: "refused", refused, cancelled };
    }
    if (error.reasonCode === "AMEND_WOULD_EMPTY") return { ok: false, why: "would-empty" };
    // A version already replaced (two tabs, after its window) is the card's end as much as a signed one.
    if (error.reasonCode === "BUNDLE_NOT_AWAITING" || error.reasonCode === "BUNDLE_SUPERSEDED") return { ok: false, why: "not-awaiting" };
  }
  return { ok: false, why: "unreachable" };
}

export function routeAmend(api: AmendApi): AmendSource {
  const send = (current: BundleView, mode: "apply" | "preview", edits: AmendEdit | readonly AmendEdit[]) =>
    api.amend(current.bundle_id, { base_sha256: current.canonical_sha256, mode, edits: Array.isArray(edits) ? edits : [edits as AmendEdit] });
  // H6: the bank names its latest version (`latest_bundle_id`); the old id reads back as the superseded version.
  const stale = async (current: BundleView, error: ApiError): Promise<Exclude<AmendResult, { readonly ok: true }>> => {
    const named = error.body["latest_bundle_id"];
    const latest = await api.bundle(typeof named === "string" && named !== "" ? named : current.bundle_id).catch(() => undefined);
    return latest === undefined ? { ok: false, why: "stale" } : { ok: false, why: "stale", latest };
  };
  return {
    async apply(current, edits) {
      try {
        const answer = await send(current, "apply", edits);
        const id = answer["bundle_id"];
        if (typeof id !== "string" || id === "") return { ok: false, why: "unreachable" };
        const bundle = await api.bundle(id);
        const changes = Array.isArray(answer["changes"]) ? (answer["changes"] as AmendChange[]) : [];
        const from = typeof answer["amended_from"] === "string" ? answer["amended_from"] : current.bundle_id;
        return { ok: true, bundle, amendedFrom: from, changes };
      } catch (error) {
        if (error instanceof ApiError && error.reasonCode === "AMEND_STALE") return stale(current, error);
        return failure(error);
      }
    },
    async preview(current, edit) {
      try {
        const answer = await send(current, "preview", edit);
        const changes = Array.isArray(answer["changes"]) ? (answer["changes"] as AmendChange[]) : [];
        const words = typeof answer["words"] === "string" ? answer["words"] : "";
        return answer["would_empty"] === true ? { ok: true, changes, words, wouldEmpty: true } : { ok: true, changes, words };
      } catch (error) {
        if (error instanceof ApiError && error.reasonCode === "AMEND_STALE") return stale(current, error);
        return failure(error);
      }
    },
  };
}

// ---------------------------------------------------------------- the stand-in bank (`?amend=fake`, tests)

/** The one rule the stand-in applies (22p's "one payment" form): a single payment above this is refused. */
const ONE_PAYMENT_CAP = 5_000_000n; // S$50,000.00 in cents


/** "half", "a third", "a quarter", else "{n}/{d}" — the share a step is of another (22q's {share}). */
function shareWord(d: AmountDerivation): string {
  const key = `${d.numerator ?? ""}/${d.denominator ?? ""}`;
  return ({ "1/2": "half", "1/3": "a third", "1/4": "a quarter", "3/4": "three quarters" } as Record<string, string>)[key] ?? key;
}

/** 22q's {Which}: a step named without its figure — "the order for DBS Group Holdings Ltd", "the payment to your Joint Account ···2280". */
function whichOf(step: SignedStep): string {
  if (step.type === "securities_order") return `the order for ${step.instrument.name}`;
  if (step.type === "funds_transfer" && step.payee === undefined && step.to_account !== undefined) {
    return `the payment to your ${step.to_account.display_name} ${lastDigits(step.to_account.masked_number)}`;
  }
  return whatOf(step).replace(/^the S\$[\d,.]+ /, "the payment ");
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

function withAmount(step: SignedStep, value: string): SignedStep {
  if (step.type === "securities_order") return { ...step, cash_amount: { ...step.cash_amount, value } };
  if (step.type === "funds_transfer" || step.type === "bill_payment") return { ...step, amount: { ...step.amount, value } };
  return step;
}

function dropDerivation(step: SignedStep): SignedStep {
  const { amount_derivation: _gone, depends_on: _deps, ...rest } = step;
  return rest as SignedStep;
}

/**
 * The signed /2 summary, as the bank would sign it again: each account's total and its projection follow the steps — an
 * account a step now comes from (F39) gets its own total and projection (`availableBefore`: what it had before this card);
 * an account no step comes from any more has neither.
 */
function summarise(summary: BundleSummary | undefined, steps: readonly SignedStep[], availableBefore: (accountId: string) => string | undefined): BundleSummary | undefined {
  if (summary === undefined) return undefined;
  const refOf = (s: SignedStep): AccountRef | undefined =>
    s.type === "securities_order" ? s.settlement_account : s.type === "funds_transfer" || s.type === "bill_payment" ? s.debit_account : undefined;
  const groups = new Map<string, { readonly ref: AccountRef; readonly steps: SignedStep[] }>();
  for (const step of steps) {
    const ref = refOf(step);
    if (ref === undefined) continue;
    const group = groups.get(ref.account_id) ?? { ref, steps: [] };
    group.steps.push(step);
    groups.set(ref.account_id, group);
  }
  const known = summary.totals.map((t) => t.account.account_id);
  const order = [...known.filter((id) => groups.has(id)), ...[...groups.keys()].filter((id) => !known.includes(id))];
  const totals = order.map((id) => {
    const group = groups.get(id) as { readonly ref: AccountRef; readonly steps: SignedStep[] };
    const was = summary.totals.find((t) => t.account.account_id === id);
    const total = { value: sumMoney(group.steps.map((s) => amountOf(s)?.value ?? "0.00")), currency: was?.total.currency ?? "SGD" };
    return { ...(was ?? {}), account: was?.account ?? group.ref, total, step_ids: group.steps.map((s) => s.step_id) };
  });
  const ids = new Set(steps.map((s) => s.step_id));
  const projected = totals.flatMap((t) => {
    const was = summary.projected.find((p) => p.account_id === t.account.account_id);
    const before = was?.available_before ?? availableBefore(t.account.account_id);
    if (before === undefined) return [];
    return [{ account_id: t.account.account_id, currency: was?.currency ?? t.total.currency, available_before: before, available_after: differenceMoney(before, t.total.value) }];
  });
  return {
    ...summary,
    totals,
    payees: summary.payees.map((p) => ({ ...p, step_ids: p.step_ids.filter((id) => ids.has(id)) })).filter((p) => p.step_ids.length > 0),
    projected,
  };
}

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

interface Applied {
  readonly steps: readonly SignedStep[];
  readonly changes: readonly AmendChange[];
  readonly refused?: RefusedStep;
}

/** One edit on a plan, as the bank would apply it — its dependants recomputed, kept or removed. */
function applyEdit(
  steps: readonly SignedStep[],
  edit: AmendEdit,
  removedSteps: (bundleId: string, stepId: string) => SignedStep | undefined,
  accounts: readonly AccountView[] = [],
): Applied {
  const target = steps.find((s) => s.step_id === edit.step_id);
  if (edit.op === "restore") {
    const back = removedSteps(edit.from_bundle_id, edit.step_id);
    if (back === undefined || target !== undefined) return { steps, changes: [] };
    const merged = [...steps, back].sort((a, b) => Number(a.index) - Number(b.index));
    return { steps: merged, changes: [{ step_id: back.step_id, change: "restored", what: whatOf(back) }] };
  }
  if (target === undefined) return { steps, changes: [] };
  const children = steps.filter((s) => s.step_id !== target.step_id && ((s.depends_on ?? []).includes(target.step_id) || s.amount_derivation?.from_step === target.step_id));
  if (edit.op === "remove") {
    const changes: AmendChange[] = [{ step_id: target.step_id, change: "removed", what: whatOf(target) }];
    let next = steps.filter((s) => s.step_id !== target.step_id);
    for (const child of children) {
      if (edit.dependants === "remove") {
        next = next.filter((s) => s.step_id !== child.step_id);
        changes.push({ step_id: child.step_id, change: "removed", what: whatOf(child), because: target.step_id });
      } else {
        const previous = amountOf(child)?.value ?? "";
        const d = child.amount_derivation;
        const was = d === undefined ? formatMoney(previous, "SGD") : d.explanation.charAt(0).toLowerCase() + d.explanation.slice(1);
        const kept = { ...dropDerivation(child), amended: { by: "customer", previous, because: target.step_id, line: `Kept by you · was ${was}` } } as SignedStep;
        next = next.map((s) => (s.step_id === child.step_id ? kept : s));
        changes.push({ step_id: child.step_id, change: "kept", what: whatOf(child), because: target.step_id });
      }
    }
    return { steps: next, changes };
  }
  if (edit.op === "set_amount") {
    const before = amountOf(target)?.value ?? "";
    const value = edit.amount.value;
    if (toMinor(value) > ONE_PAYMENT_CAP && target.type !== "securities_order") {
      return {
        steps,
        changes: [],
        refused: {
          step_id: target.step_id,
          source_index: target.source_index,
          reason_code: "AMEND_REFUSED",
          rule_ids: ["POL-LIMIT-SINGLE"],
          customer_message: `${formatMoney(value, "SGD")} is more than can go in one payment, so it wasn't changed.`,
          next_actions: [{ label: "See my limits", action: { kind: "bank_surface", surface: "limits" } }],
        },
      };
    }
    const changed = { ...dropDerivation(withAmount(target, value)), amended: { by: "customer", previous: before, line: `Changed by you · was ${formatMoney(before, "SGD")}` } } as SignedStep;
    const changes: AmendChange[] = [{ step_id: target.step_id, change: "amount", what: whatOf(target), before, after: value }];
    let next = steps.map((s) => (s.step_id === target.step_id ? changed : s));
    for (const child of children) {
      const d = child.amount_derivation;
      if (edit.dependants === "keep") {
        const previous = amountOf(child)?.value ?? "";
        const was = d === undefined ? formatMoney(previous, "SGD") : d.explanation.charAt(0).toLowerCase() + d.explanation.slice(1);
        const kept = { ...dropDerivation(child), amended: { by: "customer", previous, because: target.step_id, line: `Kept by you \u00b7 was ${was}` } } as SignedStep;
        next = next.map((s) => (s.step_id === child.step_id ? kept : s));
        changes.push({ step_id: child.step_id, change: "kept", what: whatOf(child), because: target.step_id });
        continue;
      }
      if (d?.kind !== "fraction_of_step" || d.numerator === undefined || d.denominator === undefined) continue;
      const previous = amountOf(child)?.value ?? "";
      const share = fromMinor((toMinor(value) * BigInt(d.numerator)) / BigInt(d.denominator));
      const recomputed = {
        ...withAmount(child, share),
        amount_derivation: { ...d, basis: { value, currency: "SGD" } },
        amended: { by: "bank", previous, because: target.step_id, line: `Changed with ${whichOf(changed)} · was ${formatMoney(previous, "SGD")}` },
      } as SignedStep;
      next = next.map((s) => (s.step_id === child.step_id ? recomputed : s));
      changes.push({ step_id: child.step_id, change: "amount", what: whatOf(child), before: previous, after: share, because: target.step_id });
    }
    return { steps: next, changes };
  }
  if (edit.op === "set_source") return setSource(steps, target, edit.account_id, accounts);
  return { steps, changes: [] };
}

/**
 * F39: the step's money from another of the customer's own accounts — the figure and its derivation unchanged, no 22u line
 * (row 22u has no words for it: the row's "from {account}" says it). Not enough there, with the card's other steps from it:
 * 22p's funds sentence. An account the bank does not know, the one it already comes from, or where a move goes: no edit.
 */
function setSource(steps: readonly SignedStep[], target: SignedStep, accountId: string, accounts: readonly AccountView[]): Applied {
  if (target.type !== "funds_transfer" && target.type !== "bill_payment") return { steps, changes: [] };
  const account = accounts.find((a) => a.account_id === accountId);
  const to = target.type === "funds_transfer" ? target.to_account?.account_id : undefined;
  if (account === undefined || account.account_id === to || account.account_id === target.debit_account.account_id) return { steps, changes: [] };
  const value = amountOf(target)?.value ?? "0.00";
  const already = steps.filter((s) => s.step_id !== target.step_id && (s.type === "funds_transfer" || s.type === "bill_payment") && s.debit_account.account_id === accountId);
  if (toMinor(account.available_balance) < toMinor(sumMoney([value, ...already.map((s) => amountOf(s)?.value ?? "0.00")]))) {
    return {
      steps,
      changes: [],
      refused: {
        step_id: target.step_id,
        source_index: target.source_index,
        reason_code: "AMEND_REFUSED",
        rule_ids: ["POL-BAL-SUFFICIENT"],
        customer_message: `There isn't enough in ${account.display_name} for ${formatMoney(value, "SGD")}, so it wasn't changed.`,
      },
    };
  }
  const moved = { ...target, debit_account: { account_id: account.account_id, display_name: account.display_name, masked_number: account.masked_number } } as SignedStep;
  return {
    steps: steps.map((s) => (s.step_id === target.step_id ? moved : s)),
    changes: [{ step_id: target.step_id, change: "source", what: whatOf(target), before: target.debit_account.account_id, after: account.account_id }],
  };
}

/** 22q: the bank's sentence for the dependants sheet, from what the edit would do to them. */
function previewWords(steps: readonly SignedStep[], edit: AmendEdit, applied: Applied): string {
  const target = steps.find((s) => s.step_id === edit.step_id);
  const child = applied.changes.find((c) => c.because === edit.step_id);
  const original = child === undefined ? undefined : steps.find((s) => s.step_id === child.step_id);
  if (target === undefined || child === undefined || original === undefined) return "";
  const d = original.amount_derivation;
  if (d?.kind === "shortfall_for_steps") {
    return edit.op === "remove"
      ? `${cap(whatOf(original))} was only to pay for it, so it goes too.`
      : `${cap(whichOf(original))} changes to ${formatMoney(child.after ?? "", "SGD")} to pay for it.`;
  }
  const share = d === undefined ? "part" : shareWord(d);
  return edit.op === "remove"
    ? `${cap(whatOf(original))} is ${share} of it.`
    : `${cap(whichOf(original))} is ${share} of it, so it becomes ${formatMoney(child.after ?? "", "SGD")}.`;
}

export function fakeAmend(
  o: {
    readonly now?: () => number;
    readonly latencyMs?: number;
    /** The customer's accounts, as the bank reads them (`GET /v1/accounts`): what an account change (F39) needs to know. */
    readonly accounts?: () => Promise<readonly AccountView[]>;
  } = {},
): AmendSource {
  const now = o.now ?? (() => Date.now());
  /** The design review's "Updating" state: the stand-in answers after this long (`?amend_ms=`); tests answer at once. */
  const latency = (): Promise<void> => (o.latencyMs === undefined || o.latencyMs <= 0 ? Promise.resolve() : new Promise((r) => setTimeout(r, o.latencyMs)));
  /** Every version this stand-in has seen or made, by bundle id; each lineage's latest version. */
  const versions = new Map<string, BundleView>();
  const latestOf = new Map<string, string>();
  const rootOf = new Map<string, string>();
  let made = 0;

  const remember = (b: BundleView): string => {
    const known = rootOf.get(b.bundle_id);
    if (known !== undefined) return known;
    versions.set(b.bundle_id, b);
    rootOf.set(b.bundle_id, b.bundle_id);
    latestOf.set(b.bundle_id, b.bundle_id);
    return b.bundle_id;
  };
  const removedSteps = (bundleId: string, stepId: string) => versions.get(bundleId)?.signing_payload.steps.find((s) => s.step_id === stepId);
  /** An account change (or a step restored to an account the card no longer took from) needs the bank's own read. */
  const accountsFor = async (edits: readonly AmendEdit[]): Promise<readonly AccountView[]> =>
    o.accounts !== undefined && edits.some((e) => e.op === "set_source" || e.op === "restore") ? o.accounts().catch(() => []) : [];
  /** What an account had before this card: a version's signed projection, else the bank's read. */
  const beforeOf = (accounts: readonly AccountView[]) => (accountId: string): string | undefined => {
    for (const v of versions.values()) {
      const p = v.signing_payload.bundle?.projected.find((x) => x.account_id === accountId);
      if (p !== undefined) return p.available_before;
    }
    return accounts.find((a) => a.account_id === accountId)?.available_balance;
  };

  const check = (current: BundleView): Exclude<AmendResult, { readonly ok: true }> | undefined => {
    const root = remember(current);
    const latest = versions.get(latestOf.get(root) ?? root);
    if (latest !== undefined && latest.canonical_sha256 !== current.canonical_sha256) return { ok: false, why: "stale", latest };
    // An expired card is the card's own business (its timer ends it before an edit can be made): not checked here.
    return undefined;
  };

  return {
    signable: false,
    async apply(current, edits) {
      await latency();
      const stop = check(current);
      if (stop !== undefined) return stop;
      const list: readonly AmendEdit[] = Array.isArray(edits) ? edits : [edits as AmendEdit];
      const accounts = await accountsFor(list);
      let applied: Applied = { steps: current.signing_payload.steps, changes: [] };
      for (const edit of list) {
        const one = applyEdit(applied.steps, edit, removedSteps, accounts);
        if (one.refused !== undefined) return { ok: false, why: "refused", refused: [one.refused] };
        applied = { steps: one.steps, changes: [...applied.changes, ...one.changes] };
      }
      if (applied.changes.length === 0) return { ok: false, why: "unreachable" };
      // H5: nothing left is cancelling the card — the bank's AMEND_WOULD_EMPTY.
      if (applied.steps.length === 0) return { ok: false, why: "would-empty" };
      made += 1;
      const id = `bdl_fake${String(made).padStart(4, "0")}${current.bundle_id.slice(-6)}`;
      const at = new Date(now());
      const ttl = Date.parse(current.signing_payload.expires_at) - Date.parse(current.signing_payload.created_at);
      const expires = new Date(at.getTime() + (Number.isFinite(ttl) && ttl > 0 ? ttl : 120_000)).toISOString();
      const ids = new Set(applied.steps.map((s) => s.step_id));
      const summary = summarise(current.signing_payload.bundle, applied.steps, beforeOf(accounts));
      const payload = {
        ...current.signing_payload,
        bundle_id: id,
        steps: applied.steps,
        ...(summary === undefined ? {} : { bundle: summary }),
        created_at: at.toISOString(),
        expires_at: expires,
        nonce: `fake-${String(made)}`,
      };
      const canonical = await sha256Hex(JSON.stringify(payload));
      const next: BundleView = {
        ...current,
        bundle_id: id,
        state: "awaiting_signature",
        signing_payload: payload,
        challenge: canonical.slice(0, 43),
        canonical_sha256: canonical,
        expires_at: expires,
        expired: false,
        interventions: (current.interventions ?? []).filter((i) => ids.has(i.step_id)),
      };
      const root = rootOf.get(current.bundle_id) ?? current.bundle_id;
      versions.set(id, next);
      rootOf.set(id, root);
      latestOf.set(root, id);
      return { ok: true, bundle: next, amendedFrom: current.bundle_id, changes: applied.changes };
    },
    async preview(current, edit) {
      await latency();
      const stop = check(current);
      if (stop !== undefined) return stop;
      const applied = applyEdit(current.signing_payload.steps, edit, removedSteps, await accountsFor([edit]));
      if (applied.refused !== undefined) return { ok: false, why: "refused", refused: [applied.refused] };
      const words = previewWords(current.signing_payload.steps, edit, applied);
      return applied.steps.length === 0 ? { ok: true, changes: applied.changes, words, wouldEmpty: true } : { ok: true, changes: applied.changes, words };
    },
  };
}
